import { toPairs } from 'lodash-es'

import type { SecureMessage } from './types'

/** TYPES */
type SendResponseFn = (messageId: string, data: any, targetOrigin: string) => void

interface WalletEventsQuery {
  project_id?: string
  from?: string
  to?: string
  result?: 'signed' | 'rejected'
  reason_code?: string
  chain_id?: number
  policy_id?: string
  cursor?: string
  limit?: number
}

export type WalletEventsPayload = Omit<WalletEventsQuery, 'project_id'>

export interface WalletEventRejection {
  reason_code: string
  policy_id: string
  rule: string
  attempted_wei: string | null
  limit_wei: string | null
  spent_wei: string | null
  attempted_count: number | null
  limit_count: number | null
  address: string
}

export interface WalletEventConfirmation {
  tx_hash: string
  block_number: number
  block_timestamp: string
  success: boolean
  dropped: boolean
  observed_at: string
}

export interface WalletEventView {
  event_id: string
  timestamp: string
  chain_id: number | null
  kind: 'userOp' | 'typedData' | 'message' | 'legacy'
  result: 'signed' | 'rejected'
  sender: string
  targets: string[]
  value_wei: string | null
  digest: string
  rejection: WalletEventRejection | null
  confirmation: WalletEventConfirmation | null
}

export interface WalletEventPage {
  events: WalletEventView[]
  nextCursor: string | null
}

/** SETUP */
// mints the resource token; the events themselves are read from DWH, a different service/audience
const TSS_URL =
  window?.__EMBARK_SERVICES__?.tssUrl ||
  (typeof __EMBARK_TSS_URL__ !== 'undefined' && __EMBARK_TSS_URL__) ||
  '/oops-tss-api'

const DWH_URL =
  (typeof (window as any).__EMBARK_SERVICES__ !== 'undefined' && (window as any).__EMBARK_SERVICES__?.dwhUrl) ||
  (typeof __EMBARK_DWH_URL__ !== 'undefined' && __EMBARK_DWH_URL__) ||
  '/oops-dwh-api'

// extracts the TSS `message`/`error` field from an error response, falling back to the HTTP status text
async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json()
    return body?.message || body?.error || response.statusText
  } catch {
    return response.statusText
  }
}

/** LOGIC */
export class ActivityLoggingAPI {
  private sendResponseFn: SendResponseFn

  constructor(sendResponseFn: SendResponseFn) {
    this.sendResponseFn = sendResponseFn
  }

  // API METHODS

  // DWH scopes wallet history to a resource token minted by TSS for the `embarkai-dwh-service` audience
  private async getAuditToken(projectId: string, accessToken: string): Promise<string> {
    const url = `${TSS_URL}/api/auth/token/exchange?projectId=${encodeURIComponent(projectId)}`

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      body: JSON.stringify({ scopes: ['audit:read'], audience: 'embarkai-dwh-service' })
    })

    if (!response.ok) {
      throw new Error(`Failed to get audit token: ${await readErrorMessage(response)}`)
    }

    const data = await response.json()
    if (!data?.resourceToken) {
      throw new Error('Invalid audit token response')
    }

    return data.resourceToken
  }

  private async getUserActivityLog(
    payload: WalletEventsPayload,
    project_id: string,
    accessToken: string
  ): Promise<WalletEventPage> {
    const auditToken = await this.getAuditToken(project_id, accessToken)

    const url = new URL(`${DWH_URL}/external/wallet/events`)
    const queriesSet: WalletEventsQuery = { ...payload, project_id }

    toPairs(queriesSet).forEach(([key, value]) => {
      if (value !== undefined && value !== null) url.searchParams.append(key, String(value))
    })

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auditToken}` }
    })

    if (!response.ok) {
      throw new Error(`Failed to get user activity log: ${await readErrorMessage(response)}`)
    }

    return (await response.json()) as WalletEventPage
  }

  // IFRAME MESSAGING

  public async handleMessage(message: SecureMessage, origin: string): Promise<void> {
    const { type, messageId, projectId, data } = message
    const { accessToken, payload } = data

    console.info('[ USER_ACTIVITY_LOG_API Received message ]', type, projectId, payload)

    switch (type) {
      case 'USER_ACTIVITY_LOG_GET': {
        const activityLog = await this.getUserActivityLog(payload as WalletEventsPayload, projectId, accessToken)
        this.sendResponseFn(messageId, activityLog, origin)
        break
      }

      default:
        throw new Error(`[USER_ACTIVITY_LOG_API]: Unknown message type: ${type}`)
    }
  }
}
